import {
  Avatar,
  Body1,
  Body2,
  Button,
  Caption,
  IconButton,
  Image,
  Overline,
  UIHeading2,
  UIHeading4,
} from '@cian/ui-kit';
import { IconActionClose16 } from '@cian/ui-kit-design-tokens/icons';

import { type FC, type MouseEvent as ReactMouseEvent } from 'react';
import { clsx } from 'clsx';

import { AdLabel } from 'shared/components/AdLabel';
import { MortgageAnnotations } from 'shared/components/MortgageAnnotations';
import { ScrollFade } from 'shared/components/ScrollFade';
import { Transport } from 'shared/components/Transport';

import { TGetTgbNativedescGeneralCompleteContextResult } from '../../../../utils/getTgbNativedescGeneralCompleteContext';

import styles from './TgbNativedescGeneralDesktopListing.css';

type TTgbNativedescGeneralDesktopListingProps = {
  completeContext: TGetTgbNativedescGeneralCompleteContextResult;
  onClose: (event: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void;
};

export const TgbNativedescGeneralDesktopListing: FC<TTgbNativedescGeneralDesktopListingProps> = props => {
  const {
    completeContext: {
      title,
      address,
      description,
      annotation,
      image,
      transports,
      url,
      button,
      label,
      logoImageUrl,
      builderName,
      sizes,
      mortgageWarning,
      mortgageTermsWarning,
    },
    onClose,
  } = props;

  const withMortgageAnnotations = !!(mortgageWarning || mortgageTermsWarning);

  return (
    <a
      style={{
        // Высота должна быть строго заданна из-за законодательства по ипотечным баннерам
        height: sizes.height,
        maxHeight: sizes.maxHeight,
        minHeight: sizes.minHeight,
      }}
      data-testid="tgb-nativedesc-general-desktop-listing"
      href={url}
      target="_blank"
      className={clsx(styles['container'], withMortgageAnnotations && styles['container--mortgage-annotations'])}
      rel="noopener noreferrer"
    >
      <div className={styles['content']}>
        <div className={styles['photo-block']}>
          <Image src={image} objectFit="cover" />
        </div>
        <div className={styles['main-content-block']}>
          <div className={styles['content-container']}>
            <div>
              <div className={styles['title-wrapper']}>
                <UIHeading2 as="h2" color="text-main-default">
                  {title}
                </UIHeading2>
              </div>
              <div className={styles['subtitle-wrapper']}>
                <UIHeading4 as="h4">{description}</UIHeading4>
              </div>

              <div className={styles['main-content']}>
                {!!transports.length && (
                  <div className={styles['transports-container']}>
                    {transports.map(transport => (
                      <Transport
                        key={transport.id}
                        name={transport.name}
                        remoteness={transport.remoteness}
                        iconColor={transport.iconColor}
                        iconType={transport.iconType}
                      />
                    ))}
                  </div>
                )}

                <Body2 data-testid="address" color="text-secondary-default">
                  {address}
                </Body2>
              </div>
            </div>
          </div>
          <div className={styles['footer-container']}>
            <Caption color="text-primary-disabled">{annotation}</Caption>
            <ScrollFade size={12} />
          </div>
        </div>
        <div className={styles['publisher-block']}>
          <div>
            <div className={styles['publisher-avatar-wrapper']}>
              <Avatar size={64} src={logoImageUrl} type="builder" />
            </div>

            <Overline color="text-secondary-default">Компания</Overline>

            <Body1 data-testid="builderName" fontWeight="bold">
              {builderName}
            </Body1>

            <div className={styles['more-info-button-wrapper']}>
              <Button data-testid="more-info-button" fullWidth size="XS">
                {button || 'Узнать подробности'}
              </Button>
            </div>
          </div>

          {!!label && (
            <div>
              <AdLabel label={label} />
            </div>
          )}
        </div>
        <div className={styles['close-block']} data-testid="close-block">
          <div>
            <IconButton data-testid="close-button" icon={<IconActionClose16 />} onClick={onClose} />
          </div>
        </div>
      </div>

      <MortgageAnnotations
        height={sizes.height}
        mortgageWarning={mortgageWarning}
        mortgageTermsWarning={mortgageTermsWarning}
      />
    </a>
  );
};
